import { FormikHelpers, FormikProps } from 'formik';
import { useCallback, useEffect } from 'react';
import { Alert, AlertTitle } from '@mui/material';
import styled from 'styled-components';

import useSWR from 'swr';
import { Nullable } from '@typings/global';
import { IMap } from '@shared/interfaces';
import { RESIDENT_TYPE_OPTIONS } from '@shared/constants/lists';
import { AlertModal } from '@shared/components/modals/AlertModal';

import { useConfirmationModal } from '@shared/hooks/useConfirmationModal';
import { SERVICES } from '@shared/constants/apiConfig';
import { PersonalInfo } from './components/PersonalInfo';
import { Address } from './components/Address/Address';
import { Employment } from './components/Employment';
import { References } from './components/References/References';
import { OtherIncome } from './components/OtherIncome/OtherIncome';
import { ICoApplicant } from './components/CoApplicant/validation';
import { NContact } from './validation';
import { requestDeleteCoApplicant } from './queries';
import { CoApplicants, IJointGTIResponse } from './components/CoApplicants';

const GlobalContact = styled.div`
  .MuiGrid-item {
    padding-top: 15px;
  }
`;

type FormType = Nullable<NContact.Entity | ICoApplicant>;

export const ContactTab = styled.div`
  overflow-y: auto;
`;
interface IContactFormProps extends FormikProps<FormType> {
  contactId?: string;
  onEditCoApplicant?: (coApplicantId: string) => void;
  textBtnColor?: 'error' | 'info' | 'inherit' | 'primary' | 'secondary' | 'success' | 'warning';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSave?(
    data: Partial<Nullable<NContact.Entity>> | ICoApplicant,
    formikHelpers?: FormikHelpers<NContact.Entity> | FormikHelpers<ICoApplicant>
  ): IMap<string> | Promise<any>;
  coApplicants?: NContact.CoApplicant[];
  setCoApplicantModalPosition?: React.Dispatch<React.SetStateAction<number>>;
}

export const ContactForm = ({
  contactId = '',
  values,
  setFieldValue,
  onEditCoApplicant,
  onSave,
  setSubmitting,
  setCoApplicantModalPosition,
  resetForm,
  coApplicants = [],
}: IContactFormProps) => {
  const { isOpen: isOpenDeleteConfirmModal, onCancel, onConfirm, openDialog } = useConfirmationModal();

  const contactIds = coApplicants.map((coApplicant) => coApplicant.id).concat(contactId ? [contactId] : []);

  const jointDtiUrl = contactIds.length
    ? `${SERVICES.CONTACT}/joint-dti?${contactIds.map((id) => `contactIds=${id}`).join('&')}`
    : null;

  const { data: jointDtiResponse, mutate } = useSWR<IJointGTIResponse>(jointDtiUrl, {
    revalidateOnMount: true,
  });

  const onRowSave = useCallback(
    async (fieldName: keyof FormType, data: FormType[keyof FormType], formikValues: NContact.Entity | ICoApplicant) => {
      await setFieldValue(fieldName, data);
      const helpers = { resetForm, setSubmitting } as FormikHelpers<NContact.Entity> | FormikHelpers<ICoApplicant>;
      return await onSave?.({ ...formikValues, [fieldName]: data }, helpers);
    },
    [onSave, mutate]
  );

  const handleRemoveCoApplicant = useCallback(async (coApplicantIdToDelete: string) => {
    const result = await openDialog();

    if (result) {
      await requestDeleteCoApplicant(contactId, coApplicantIdToDelete);
    }
  }, []);

  useEffect(() => {
    if (values.ssn?.startsWith('9') && values?.residencyStatusType !== RESIDENT_TYPE_OPTIONS[2].value) {
      setFieldValue('residencyStatusType', RESIDENT_TYPE_OPTIONS[2].value);
    }
  }, [values.ssn, values?.residencyStatusType]);

  return (
    <>
      {isOpenDeleteConfirmModal && (
        <AlertModal
          open
          title="Delete Co-Applicant"
          subtitle="Are you sure you want to delete this Co-Applicant?"
          onConfirm={onConfirm}
          onClose={onCancel}
        />
      )}

      <GlobalContact>
        <ContactTab>
          <PersonalInfo />

          {coApplicants.length ? (
            <CoApplicants
              data={coApplicants}
              contactId={contactId}
              setCoApplicantModalPosition={setCoApplicantModalPosition}
              jointDtiResponse={jointDtiResponse}
              onEdit={onEditCoApplicant}
              onRemove={handleRemoveCoApplicant}
            />
          ) : null}

					{values?.id ?
						<>
							<Address
								data={values?.addresses || []}
                mutateCombinedDti={mutate}
								onChange={async(data) => onRowSave('addresses', data, values)}
							/>

							<Employment
								data={values?.employments || []}
                mutateCombinedDti={mutate}
								onChange={async(data) => onRowSave('employments', data, values)}
							/>

							<OtherIncome
								data={values?.otherIncomes || []}
                mutateCombinedDti={mutate}
								onChange={async(data) => onRowSave('otherIncomes', data, values)}
							/>

							<References
								data={values?.references || []}
								onChange={async(data) => onRowSave('references', data, values)}
							/>
						</> :
						<Alert severity="info"><AlertTitle> Add personal information and save before editing Address/Employment/Income/Expenses/References</AlertTitle></Alert>
					}
				</ContactTab>
			</GlobalContact>
		</>
	);
};
